import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ViewChild,
  OnDestroy,
  OnInit,
  HostListener,
  ChangeDetectionStrategy,
  WritableSignal,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  FormGroup,
} from '@angular/forms';
import {
  NavigationStart,
  Router,
  ActivatedRoute,
  RouterModule,
} from '@angular/router';
import {
  RdsBadgeModule,
  RdsButtonModule,
  RdsCheckboxModule,
  RdsChipEditedEvent,
  RdsChipInputEvent,
  RdsChipsModule,
  RdsDialogService,
  RdsDropdownModule,
  RdsFormFieldModule,
  RdsIconsModule,
  RdsLinkModule,
  RdsListItemModule,
  RdsMultiAutocompleteComponent,
  RdsProgressSpinnerComponent,
  RdsSingleSelectFilterByFunc,
  RdsSingleSelectOptionComponent,
  RdsStepperModule,
  RdsTableDataSource,
  RdsTableModule,
  RdsUploadModule,
} from '@rds/angular-components';
import { OfferingType } from '../../../enums/offering-type.enum';
import { OfferingsService } from '../../../services/offerings.service';
import { MulesoftOffering } from '../../../models/mulesoft-offering';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, filter, takeUntil, debounceTime } from 'rxjs/operators';
import { Subject, merge } from 'rxjs';
import { mapMulesoftOfferingToServiceForm } from '../../../mappers/mulesoft-offering-form.mapper';
import { ApprovalStatus } from '../../../enums/approval-status.enum';
import { UiService } from '../../../services/ui.service';
import {
  OfferingFeature,
  OfferingUseCase,
  OfferingInstallation,
  Offering,
  OfferingAvailableVersion,
  OfferingSupportChannel,
  OfferingSupportHour,
  OfferingEnvironment,
  OfferingAgent,
  OfferingCapability,
  OfferingCommunity,
  OfferingRelatedTechnology,
  OfferingHosting,
  OfferingServiceAndRequest,
  OfferingOnboardingStep,
  OfferingAddonDetail,
} from '../../../models/offering';
import { AddonCost } from '../../../enums/addon-cost.enum';
import { HostingManagement } from '../../../enums/hosting-management.enum';
import { BUTTON_TEXTS } from '../../../constants/buttons.constants';
import { ERRORS_TEXTS } from '../../../constants/errors.contstants';
import {
  decimalValidator,
  strictEmailValidator,
  emailListValidator,
  slugValidator,
  slugify,
  urlValidator,
} from '../../../validators';
import { noDuplicateEmailsValidator } from '../../../validators/no-duplicate-emails.validator';
import { AnalyticsService } from '../../../services/analytics.service';
import { FeatureFlagDirective } from '../../../directives/feature-flag.directive';
import { CommonModule } from '@angular/common';
import { MulesoftService } from '../../../services/mulesoft.service';
import { SERVICE_FORM_TEXTS } from '../../../constants/service-form.constants';
import { buildMulesoftServiceDTO } from '../../../mappers/mulesoft.mapper';
import { POPUPS } from '../../../constants/popups.constants';
import { isDuplicateNameError } from '../../../util/duplicate-name-error.util';
import { attachDirtyDialogGuard } from '../../../guards/dirty-dialog.guard';
import { RichTextEditorComponent } from '../../shared/rich-text-editor/rich-text-editor.component';
import { FormSkeletonComponent } from '../form-skeleton/form-skeleton.component';

@Component({
  selector: 'inexp-focus-service-form',
  standalone: true,
  imports: [
    RdsIconsModule,
    RdsButtonModule,
    RdsStepperModule,
    ReactiveFormsModule,
    RdsFormFieldModule,
    RdsUploadModule,
    RdsCheckboxModule,
    RdsTableModule,
    RdsDropdownModule,
    RdsListItemModule,
    RdsChipsModule,
    RdsLinkModule,
    RouterModule,
    RdsProgressSpinnerComponent,
    FeatureFlagDirective,
    RdsMultiAutocompleteComponent,
    CommonModule,
    RdsBadgeModule,
    RichTextEditorComponent,
    FormSkeletonComponent,
  ],
  templateUrl: './focus-service-form.component.html',
  styleUrl: './focus-service-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FocusServiceFormComponent implements OnInit, OnDestroy {
  // Services
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _offeringsService = inject(OfferingsService);
  private _offeringLoaded = false;
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _mulesoftService = inject(MulesoftService);
  private readonly _uiService = inject(UiService);
  private readonly _dialog = inject(RdsDialogService);
  private readonly _analyticsService = inject(AnalyticsService);
  // UI
  private readonly _fieldErrorHistory = new Map<string, number>();

  // Validation array
  private validationErrors: string[] = [];

  // Cleanup
  private readonly destroy$ = new Subject<void>();

  // Data sources
  readonly useCaseDataSource = new RdsTableDataSource<OfferingUseCase>().init();
  readonly agentDataSource = new RdsTableDataSource<OfferingAgent>().init();
  readonly capabilityDataSource =
    new RdsTableDataSource<OfferingCapability>().init();
  readonly communityDataSource =
    new RdsTableDataSource<OfferingCommunity>().init();
  readonly relatedTechnologyDataSource =
    new RdsTableDataSource<OfferingRelatedTechnology>().init();
  readonly serviceAndRequestDataSource =
    new RdsTableDataSource<OfferingServiceAndRequest>().init();
  readonly onboardingDataSource =
    new RdsTableDataSource<OfferingOnboardingStep>().init();

  // Constants
  readonly offeringTypes = Object.values(OfferingType);
  readonly approvalStatuses = Object.values(ApprovalStatus);
  ApprovalStatus = ApprovalStatus;
  readonly OFFERING_FORM = SERVICE_FORM_TEXTS;
  readonly BUTTONS = BUTTON_TEXTS;
  readonly ERRORS = ERRORS_TEXTS;
  readonly POPUPS = POPUPS;

  readonly onboardingColumns = ['description', 'referenceUrl'];
  readonly useCaseColumns = ['name', 'description'];
  readonly agentColumns = ['name', 'url'];
  readonly capabilityColumns = ['name', 'description', 'benefit'];
  readonly communityColumns = ['title', 'description', 'url'];
  readonly relatedTechnologyColumns = ['targetId', 'description'];
  readonly serviceAndRequestColumns = [
    'type',
    'title',
    'destinationValue',
    'description',
  ];

  // Form abandon tracking
  private isFormAbandonNavigateLogged = false;
  private isFormAbandonCloseLogged = false;
  private isFormSavedOrPublished = false;
  isPublishing = signal(false);
  isDraftSaving = signal(false);

  // Signals
  authors = signal<string[]>([]);
  capabilities = signal<OfferingCapability[]>([]);
  useCases = signal<OfferingUseCase[]>([]);
  agents = signal<OfferingAgent[]>([]);
  communities = signal<OfferingCommunity[]>([]);
  relatedTechnology = signal<OfferingRelatedTechnology[]>([]);
  servicesAndRequests = signal<OfferingServiceAndRequest[]>([]);
  onboardingSteps = signal<OfferingOnboardingStep[]>([]);
  offerings = this._offeringsService.offerings;
  offeringId = signal<string>('');
  offeringSlug = signal<string>('');
  offeringName = signal<string>('');
  knowledge = signal<string>('');
  hasUserInteracted = signal(false);
  similarOfferingUuidsFromMule = signal<string[]>([]);

  // Route parameter
  offeringUUID = toSignal(
    this._route.paramMap.pipe(map((params) => params.get('uuid'))),
    { initialValue: this._route.snapshot.paramMap.get('uuid') },
  );

  // Form entry analytics + create-mode initialisation (runs once when uuid resolves)
  private readonly logAndLoad = effect(() => {
    const offeringUUID = this.offeringUUID();

    if (!offeringUUID) {
      this.setInitialFormValues();
    }

    // Event: form_entry
    this._analyticsService.pushFormEntryEvent(
      offeringUUID ?? '',
      OfferingType.PROFESSIONAL_SERVICE,
    );
  });

  // Fetch the offering for editing directly by uuid once the route resolves.
  private readonly loadOfferingEffect = effect(() => {
    const offeringUUID = this.offeringUUID();
    if (!offeringUUID || this._offeringLoaded) return;

    this._offeringLoaded = true;
    this.loadOfferingById(offeringUUID);
  });

  // Fetch a single draft offering and patch the form with it.
  private loadOfferingById(uuid: string): void {
    this._offeringsService
      .fetchDraftOffering(uuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe((offering) => {
        if (!offering) return;
        this.draftOffering.set(offering);
        this.loadOffering(offering);
      });
  }

  /**
   * After a successful write, overlay the authoritative response onto the loaded
   * offering (ids, slug, timestamps) instead of re-reading — the read endpoint is
   * cache-lagged right after a write and would revert the form to stale data. The
   * form already holds the saved values, so form controls are left untouched.
   */
  private syncAfterSave(response: { offering?: MulesoftOffering } | null): void {
    const saved = response?.offering;
    if (!saved) return;
    this.draftOffering.update((prev) => (prev ? { ...prev, ...saved } : saved));
    // Keep the just-saved offering visible in "my offerings" despite the
    // cache-lagged list refresh.
    this._offeringsService.upsertDraftOptimistic(this.draftOffering());
  }

  private readonly syncSimilarOfferingsEffect = effect(() => {
    const offerings = this.offerings();
    const uuids = this.similarOfferingUuidsFromMule();

    // Wait until both are available
    if (!offerings.length || !uuids.length) return;

    // map UUIDs to Offering objects
    const mappedOfferings = uuids
      .map((ref) => offerings.find((o) => String(o.id) === ref || o.uuid === ref))
      .filter((o): o is Offering => !!o);

    const currentValue = this.formOverview.getRawValue().similarOfferings;

    // avoid unnecessary patch
    if (
      Array.isArray(currentValue) &&
      currentValue.length === mappedOfferings.length &&
      currentValue.every((o: Offering) =>
        mappedOfferings.some((m) => m.uuid === o.uuid),
      )
    ) {
      return;
    }

    // Patch form control with the mapped full Offering objects
    this.formOverview.patchValue({ similarOfferings: mappedOfferings });
    this.formOverview.get('similarOfferings')?.markAsPristine();

    // Update original form values so the resolved offerings are not seen as a user change
    this._analyticsService.setOriginalFormValues({
      formOverview: this.formOverview.getRawValue(),
      formDetails: this.formDetails.getRawValue(),
      formFunctionality: this.formFunctionality.getRawValue(),
      formPermissions: this.formPermissions.getRawValue(),
    });
  });

  formPermissions = this._fb.group(
    {
      ownerEmail: ['', [Validators.required, strictEmailValidator()]],
      deputyEmail: ['', [Validators.required, strictEmailValidator()]],
      authorEmails: [
        [] as string[],
        [Validators.required, emailListValidator()],
      ],
    },
    {
      validators: [noDuplicateEmailsValidator()],
      updateOn: 'blur',
    },
  );

  // Form groups
  formOverview = this._fb.group({
    displayName: ['', [Validators.required]],
    approvalStatus: ['', Validators.required],
    successor: [''],
    description: ['', Validators.required],
    similarOfferings: [[] as Offering[]],
  });

  formDetails = this._fb.group({
    onboardingAccessInstructions: ['', Validators.required],
    onboardingSteps: [[] as OfferingOnboardingStep[]],
    agents: [[] as OfferingAgent[]],
    communities: [[] as OfferingCommunity[]],
    relatedTechnology: [[] as OfferingRelatedTechnology[]],
    servicesAndRequests: [[] as OfferingServiceAndRequest[]],
    vendorName: [''],
    vendorSupport: [''],
    vendorUrl: ['', urlValidator()],
    isAdditionalCostConsiderationsAvailable: [false],
    additionalCostConsiderations: [''],
    isOtherPricingModelsAvailable: [false],
    otherPricingModels: [''],
  });

  formFunctionality = this._fb.group({
    capabilities: [[] as OfferingCapability[]],
    useCases: [[] as OfferingUseCase[]],
  });

  // Computed summary (only if needed in template)
  readonly summary = computed(() => ({
    overview: this.overviewSig(),
    details: this.detailsSig(),
    permissions: this.permissionsSig(),
    useCases: this.useCases(),
    relatedTechnology: this.relatedTechnology(),
    servicesAndRequests: this.servicesAndRequests(),
    onboardingSteps: this.onboardingSteps(),
    capabilities: this.capabilities()
  }));
  /** The draft offering fetched for editing (direct by-uuid fetch). */
  readonly draftOffering = signal<MulesoftOffering | undefined>(undefined);
  readonly offering = computed(() => this.draftOffering());
  /** Edit mode + draft offering not yet loaded → show form skeleton. */
  readonly isLoading = computed(() => !!this.offeringUUID() && !this.draftOffering());

  // Form value signals
  readonly overviewSig = toSignal(
    this.formOverview.valueChanges.pipe(
      debounceTime(300),
      map(() => this.formOverview.getRawValue()),
    ),
    { initialValue: this.formOverview.getRawValue() },
  );

  readonly detailsSig = toSignal(
    this.formDetails.valueChanges.pipe(
      debounceTime(300),
      map(() => this.formDetails.getRawValue()),
    ),
    { initialValue: this.formDetails.getRawValue() },
  );

  readonly permissionsSig = toSignal(
    this.formPermissions.valueChanges.pipe(
      debounceTime(300),
      map(() => this.formPermissions.getRawValue()),
    ),
    { initialValue: this.formPermissions.getRawValue() },
  );

  ngOnInit(): void {
    this.setupRouteChangeTracking();
    this.setupCacheInvalidation();
    this.setupConditionalValidation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._analyticsService.reset();
  }

  // Setup methods
  private setupConditionalValidation(): void {
    // Define all conditional field relationships
    const conditionalFields = [
      {
        checkboxControl: 'isAdditionalCostConsiderationsAvailable',
        dependentControl: 'additionalCostConsiderations',
        validators: [Validators.required],
      },
      {
        checkboxControl: 'isOtherPricingModelsAvailable',
        dependentControl: 'otherPricingModels',
        validators: [Validators.required],
      },
    ];

    // Set up validation for each conditional field
    for (const {
      checkboxControl,
      dependentControl,
      validators,
    } of conditionalFields) {
      this.setupConditionalField(checkboxControl, dependentControl, validators);
    }
  }

  private setupConditionalField(
    checkboxControlName: string,
    dependentControlName: string,
    validators: any[],
  ): void {
    this.formDetails
      .get(checkboxControlName)
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((isChecked) => {
        const dependentControl = this.formDetails.get(dependentControlName);

        if (isChecked) {
          dependentControl?.setValidators(validators);
        } else {
          dependentControl?.clearValidators();
          dependentControl?.setValue('');
        }

        dependentControl?.updateValueAndValidity();
      });
  }

  readonly getOfferingById = computed(() => {
    const offeringsMap = new Map(
      this.offerings().map((offering) => [offering.uuid, offering]),
    );

    return (id: string | null | undefined) => {
      if (!id) return null;
      return offeringsMap.get(id);
    };
  });

  readonly similarOfferingOptions = (): Offering[] => {
    // Return all offerings, excluding the current one to avoid self-selection
    const currentUuid = this.offeringUUID();
    return this.offerings().filter((o) => o.uuid !== currentUuid);
  };

  readonly compareOfferings = (o1: Offering, o2: Offering): boolean => {
    if (!o1 || !o2) return false;
    return o1.uuid === o2.uuid;
  };

  readonly filterOfferings = (offering: Offering, query: string): boolean => {
    if (!query) return true; // show all if empty
    return offering.displayName.toLowerCase().includes(query.toLowerCase());
  };

  // UI summary: comma-separated display names
  readonly similarOfferingsDisplay = computed((): string => {
    const similarOfferingsRaw = this.summary().overview.similarOfferings;
    let similarOfferingsArray: any[];
    if (Array.isArray(similarOfferingsRaw)) {
      similarOfferingsArray = similarOfferingsRaw;
    } else if (similarOfferingsRaw) {
      similarOfferingsArray = Object.values(similarOfferingsRaw);
    } else {
      similarOfferingsArray = [];
    }

    const names = similarOfferingsArray
      .filter(
        (o): o is Offering =>
          !!o && typeof o === 'object' && 'displayName' in o,
      )
      .map((o) => o.displayName)
      .filter(Boolean);

    return names.length ? names.join(', ') : '—';
  });

  private setupRouteChangeTracking(): void {
    this._router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.trackFormAbandonNavigate());
  }

  private setupCacheInvalidation(): void {
    merge(
      this.formOverview.valueChanges,
      this.formDetails.valueChanges,
      this.formFunctionality.valueChanges,
      this.formPermissions.valueChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this._analyticsService.invalidateCache();
      });
  }

  // Loading offering
  loadOffering(sourceOffering: MulesoftOffering): void {
    try {
        const offering = mapMulesoftOfferingToServiceForm(sourceOffering);
        this.offeringId.set(offering.id);
        this.knowledge.set(offering.knowledge);
        this.offeringSlug.set(sourceOffering.slug ?? '');
        this.offeringName.set(
          sourceOffering.offeringName ?? sourceOffering.displayName ?? '',
        );

        // Set signals FIRST
        this.authors.set(offering.permissions.authorEmails ?? []);
        this.capabilities.set(offering.functionality.capabilities ?? []);
        this.useCases.set(offering.functionality.useCases ?? []);
        this.agents.set(offering.details.agents ?? []);
        this.communities.set(offering.details.communities ?? []);
        this.relatedTechnology.set(offering.details.relatedTechnology ?? []);
        this.servicesAndRequests.set(
          offering.details.servicesAndRequests ?? [],
        );
        this.onboardingSteps.set(offering.details.onboardingSteps ?? []);

        this.similarOfferingUuidsFromMule.set(
          offering.overview.similarOfferings ?? [],
        );
        // Then patch forms with the SAME data including synced arrays
        this.formOverview.patchValue({
          ...offering.overview,
        });
        this.formDetails.patchValue(offering.details);
        this.formFunctionality.patchValue({
          capabilities: offering.functionality.capabilities ?? [],
          useCases: offering.functionality.useCases ?? [],
        });
        this.formPermissions.patchValue({
          ...offering.permissions,
          authorEmails: this.authors(),
        });
        this.capabilityDataSource.data = this.capabilities();
        this.useCaseDataSource.data = this.useCases();
        this.agentDataSource.data = this.agents();
        this.communityDataSource.data = this.communities();
        this.relatedTechnologyDataSource.data = this.relatedTechnology();
        this.serviceAndRequestDataSource.data = this.servicesAndRequests();
        this.onboardingDataSource.data = this.onboardingSteps();

        // Store the initial form values for comparison
        this._analyticsService.setOriginalFormValues({
          formOverview: this.formOverview.value,
          formDetails: this.formDetails.value,
          formFunctionality: this.formFunctionality.value,
          formPermissions: this.formPermissions.value,
        });

        this.formOverview.markAsPristine();
        this.formDetails.markAsPristine();
        this.formFunctionality.markAsPristine();
        this.formPermissions.markAsPristine();
        this.hasUserInteracted.set(false);
    } catch {
      this._uiService.showErrorAlert('Error', 'Failed to load offering data');
      this._router.navigate(['/focus/my-offerings']);
    }
  }

  // Generic dialog handler to reduce code duplication
  private async handleDialog<T>(
    componentLoader: () => Promise<any>,
    dataKey: string,
    signal: WritableSignal<T[]>,
    formControlName: string,
    dataSource: RdsTableDataSource<T>,
    item?: T,
    additionalData?: any,
  ): Promise<void> {
    try {
      const componentModule = await componentLoader();
      const Component = componentModule[Object.keys(componentModule)[0]];

      const dialogData: any = { [dataKey]: item };
      if (additionalData) {
        Object.assign(dialogData, additionalData);
      }

      const dialogRef = this._dialog.open(Component, {
        size: 'm',
        data: dialogData,
        disableClose: true,
      });

      attachDirtyDialogGuard(dialogRef);

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            if (data) {
              signal.update((arr) => {
                if (item) {
                  // Edit existing item
                  const index = arr.indexOf(item);
                  if (index > -1) {
                    return [
                      ...arr.slice(0, index),
                      { ...item, ...data },
                      ...arr.slice(index + 1),
                    ];
                  }
                  return arr;
                } else {
                  // Add new item
                  return [...arr, data];
                }
              });

              // Update form control
              const control = this.getFormControl(formControlName);
              if (control) {
                control.patchValue(signal());
              }

              dataSource.data = signal();
            }
          },
        });
    } catch (error) {
      console.error('Error loading dialog component:', error);
    }
  }

  private getFormControl(path: string): AbstractControl | null {
    if (path.startsWith('formDetails.')) {
      return this.formDetails.get(path.replace('formDetails.', ''));
    } else if (path.startsWith('formFunctionality.')) {
      return this.formFunctionality.get(path.replace('formFunctionality.', ''));
    }
    return null;
  }

  // Dialog handlers

  async handleCapabilityDialog(capability?: OfferingCapability): Promise<void> {
    await this.handleDialog(
      () =>
        import('../dialogs/focus-capability-dialog/focus-capability-dialog.component'),
      'capability',
      this.capabilities,
      'formFunctionality.capabilities',
      this.capabilityDataSource,
      capability,
    );
  }

  async handleUseCaseDialog(useCase?: OfferingUseCase): Promise<void> {
    await this.handleDialog(
      () =>
        import('../dialogs/focus-use-case-dialog/focus-use-case-dialog.component'),
      'useCase',
      this.useCases,
      'formFunctionality.useCases',
      this.useCaseDataSource,
      useCase,
    );
  }

  async handleOnboardingDialog(step?: OfferingOnboardingStep): Promise<void> {
    await this.handleDialog(
      () =>
        import('../dialogs/focus-onboarding-dialog/focus-onboarding-dialog.component'),
      'onboardingStep',
      this.onboardingSteps,
      'formDetails.onboardingSteps',
      this.onboardingDataSource,
      step,
    );
  }

  handleAgentDialog(agent?: OfferingAgent): Promise<void> {
    return this.handleDialog(
      () =>
        import('../dialogs/focus-agent-dialog/focus-agent-dialog.component'),
      'agent',
      this.agents,
      'formDetails.agents',
      this.agentDataSource,
      agent,
    );
  }

  handleCommunityDialog(community?: OfferingCommunity): Promise<void> {
    return this.handleDialog(
      () =>
        import('../dialogs/focus-community-dialog/focus-community-dialog.component'),
      'community',
      this.communities,
      'formDetails.communities',
      this.communityDataSource,
      community,
    );
  }

  handleRelatedTechnologyDialog(
    relatedTechnology?: OfferingRelatedTechnology,
  ): Promise<void> {
    return this.handleDialog(
      () =>
        import(
          '../dialogs/focus-related-technology-dialog/focus-related-technology-dialog.component'
        ),
      'relatedTechnology',
      this.relatedTechnology,
      'formDetails.relatedTechnology',
      this.relatedTechnologyDataSource,
      relatedTechnology,
      {
        offerings: this.similarOfferingOptions(),
      },
    );
  }

  handleServiceAndRequestDialog(
    request?: OfferingServiceAndRequest,
  ): Promise<void> {
    return this.handleDialog(
      () =>
        import('../dialogs/focus-service-and-request-dialog/focus-service-and-request-dialog.component'),
      'serviceAndRequest',
      this.servicesAndRequests,
      'formDetails.servicesAndRequests',
      this.serviceAndRequestDataSource,
      request,
    );
  }


  // Delete handlers
  handleDeleteCapability(capability: OfferingCapability): void {
    this.deleteItem(
      this.capabilities,
      capability,
      this.formFunctionality.get('capabilities'),
      this.capabilityDataSource,
    );
  }

  handleDeleteUseCase(useCase: OfferingUseCase): void {
    this.deleteItem(
      this.useCases,
      useCase,
      this.formFunctionality.get('useCases'),
      this.useCaseDataSource,
    );
  }

  handleDeleteOnboardingStep(step: OfferingOnboardingStep): void {
    this.deleteItem(
      this.onboardingSteps,
      step,
      this.formDetails.get('onboardingSteps'),
      this.onboardingDataSource,
    );
  }

  handleDeleteAgent(agent: OfferingAgent): void {
    this.deleteItem(
      this.agents,
      agent,
      this.formDetails.get('agents'),
      this.agentDataSource,
    );
  }



  handleDeleteCommunity(community: OfferingCommunity): void {
    this.deleteItem(
      this.communities,
      community,
      this.formDetails.get('communities'),
      this.communityDataSource,
    );
  }


  handleDeleteRelatedTechnology(
    relatedTechnology: OfferingRelatedTechnology,
  ): void {
    this.deleteItem(
      this.relatedTechnology,
      relatedTechnology,
      this.formDetails.get('relatedTechnology'),
      this.relatedTechnologyDataSource,
    );
  }


  handleDeleteServiceAndRequest(request: OfferingServiceAndRequest): void {
    this.deleteItem(
      this.servicesAndRequests,
      request,
      this.formDetails.get('servicesAndRequests'),
      this.serviceAndRequestDataSource,
    );
  }

  private deleteItem<T>(
    signal: WritableSignal<T[]>,
    item: T,
    formControl: AbstractControl | null,
    dataSource: RdsTableDataSource<T>,
  ): void {
    if (!formControl) return;
    this._uiService
      .showDeleteDialog(
        POPUPS.DELETE.TITLE,
        POPUPS.DELETE.DESCRIPTION,
        POPUPS.DELETE.WARNING,
        BUTTON_TEXTS.DELETE,
        BUTTON_TEXTS.CANCEL,
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          signal.update((arr) => arr.filter((i) => i !== item));
          formControl.patchValue(signal());
          dataSource.data = signal();
        }
      });
  }

  private getCurrentFormValues(): any {
    return {
      formOverview: this.formOverview.getRawValue(),
      formDetails: this.formDetails.getRawValue(),
      formFunctionality: this.formFunctionality.getRawValue(),
      formPermissions: this.formPermissions.getRawValue(),
    };
  }

  private getAllFormControls(): Record<string, AbstractControl> {
    return {
      ...this.formOverview.controls,
      ...this.formDetails.controls,
      ...this.formFunctionality.controls,
      ...this.formPermissions.controls,
    };
  }

  // Call this method to update error history
  private updateErrorHistory(): void {
    const controls = this.getAllFormControls();

    for (const [fieldName, control] of Object.entries(controls)) {
      if (control.invalid && control.errors) {
        const currentCount = this._fieldErrorHistory.get(fieldName) ?? 0;
        this._fieldErrorHistory.set(fieldName, currentCount + 1);
      }
    }
  }

  // Analytics events
  handleSuccessEvent(): void {
    this._analyticsService.pushFormSuccessEvent(
      this.offeringId() ?? '',
      this.getCurrentFormValues(),
      OfferingType.PROFESSIONAL_SERVICE,
    );
  }

  handleErrorEvent(): void {
    this.updateErrorHistory();
    this._analyticsService.pushFormErrorEvent(
      this.offeringId() ?? '',
      this.getCurrentFormValues(),
      this.getAllFormControls(),
      this._fieldErrorHistory,
      OfferingType.PROFESSIONAL_SERVICE,
    );
  }

  handleDraftEvent(): void {
    this.updateErrorHistory();
    this._analyticsService.pushDraftSaveEvent(
      this.offeringId() ?? '',
      this.getCurrentFormValues(),
      this.getAllFormControls(),
      this._fieldErrorHistory,
      OfferingType.PROFESSIONAL_SERVICE,
    );
  }

  private trackFormAbandonNavigate(): void {
    if (this.isFormAbandonNavigateLogged || this.isFormSavedOrPublished) {
      return;
    }
    this.isFormAbandonNavigateLogged = true;

    this._analyticsService.pushFormAbandonNavigateEvent(
      this.offeringId() ?? '',
      this.getCurrentFormValues(),
      this.getAllFormControls(),
      OfferingType.PROFESSIONAL_SERVICE,
    );
  }

  private trackFormAbandonClose(): void {
    if (this.isFormAbandonCloseLogged || this.isFormSavedOrPublished) {
      return;
    }
    this.isFormAbandonCloseLogged = true;

    this._analyticsService.pushFormAbandonCloseEvent(
      this.offeringId() ?? '',
      this.getCurrentFormValues(),
      this.getAllFormControls(),
      OfferingType.PROFESSIONAL_SERVICE,
    );
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): string | void {
    const currentValues = this.getCurrentFormValues();
    const changedFields =
      this._analyticsService['getChangedFields'](currentValues);

    if (changedFields.length > 0) {
      this.trackFormAbandonClose();
      $event.preventDefault();

      // Set returnValue only if it exists (for legacy browser support)
      if ('returnValue' in $event) {
        ($event as any).returnValue = '';
      }

      return '';
    }
  }

  canDeactivate(): boolean {
    if (this.isFormSavedOrPublished) {
      return true;
    }

    const currentValues = this.getCurrentFormValues();
    const changedFields =
      this._analyticsService['getChangedFields'](currentValues);
    if (changedFields.length > 0) {
      return confirm(
        'You have unpublished changes. Do you really want to leave without publishing?',
      );
    }
    return true;
  }

  markFormAsSavedOrPublished(): void {
    this.isFormSavedOrPublished = true;
  }

  onAuthorFocusOut(input: HTMLInputElement): void {
    const value = (input.value ?? '').trim().toLowerCase();

    if (value) {
      // commit the pending input into authors array & form
      this.authors.update((authors) => [...authors, value]);
      this.formPermissions.patchValue({ authorEmails: this.authors() });
      input.value = '';
    }

    const control = this.formPermissions.get('authorEmails');
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  private setInitialFormValues(): void {
    this._analyticsService.setOriginalFormValues({
      formOverview: this.formOverview.getRawValue(),
      formDetails: this.formDetails.getRawValue(),
      formFunctionality: this.formFunctionality.getRawValue(),
      formPermissions: this.formPermissions.getRawValue(),
    });
  }

  // Form submission
  handlePublish(): void {
    if (!this.validateAllForms()) {
      const missingFieldsList = this.validationErrors.join(', ');

      const detailedDescription = `${POPUPS.ERROR.DESCRIPTION} ${missingFieldsList}.`;
      this._uiService.showErrorAlert(
        POPUPS.ERROR.TITLE,
        detailedDescription,
      );
      this.handleErrorEvent();
      return;
    }

    const offeringUUID = this.offeringUUID();
    const offeringId = this.offeringId();
    const knowledge = this.knowledge();

    const mulesoftServiceDTO = buildMulesoftServiceDTO(
      offeringId,
      knowledge,
      this.formOverview,
      this.formDetails,
      this.formFunctionality,
      this.formPermissions,
      true,
    );

    this.isPublishing.set(true);

    const request$ = offeringUUID
      ? this._mulesoftService.editProfessionalService(mulesoftServiceDTO)
      : this._mulesoftService.createProfessionalService(mulesoftServiceDTO);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        this.isPublishing.set(false);
        this.formOverview.markAsPristine();
        this.formDetails.markAsPristine();
        this.formFunctionality.markAsPristine();
        this.formPermissions.markAsPristine();

        this._uiService.showSuccessAlert(
          'Success',
          'Your main offering content has been published!',
          '/focus/my-offerings',
          BUTTON_TEXTS.BACK_TO_MY_OFFERINGS,
          BUTTON_TEXTS.CLOSE,
        );

        this.handleSuccessEvent();
        this.markFormAsSavedOrPublished();
        if (offeringUUID) {
          // Do NOT re-read after a write — the read endpoint is cache-lagged
          // and would revert the form to stale data. Overlay the authoritative
          // write response instead; the form already holds the saved values.
          this.syncAfterSave(response);
        } else if (response?.offering?.uuid) {
          this._offeringsService.upsertDraftOptimistic(response.offering);
          this._router.navigate(
            ['/focus/offering/professional-service', response.offering.uuid],
            { replaceUrl: true },
          );
        }
      },
      error: (err) => {
        this.isPublishing.set(false);
        if (isDuplicateNameError(err)) {
          this._uiService.showErrorAlert(
            POPUPS.DUPLICATE_NAME.DUPLICATE_NAME_TITLE,
            POPUPS.DUPLICATE_NAME.DUPLICATE_OFFERING_DESCRIPTION,
          );
        } else {
          this._uiService.showErrorAlert(
            'Error',
            'There was an error publishing your offering',
          );
        }
        console.error('MuleSoft publish error:', err);
        this.handleErrorEvent();
      },
    });
  }

  handleDraftSave(): void {
    if (!this.validateDraft()) {
      this._uiService.showErrorAlert(
        POPUPS.DRAFT_ERROR.TITLE,
        POPUPS.DRAFT_ERROR.DESCRIPTION,
      );
      return;
    }

    const offeringUUID = this.offeringUUID();
    const offeringId = this.offeringId();
    const knowledge = this.knowledge();

    const mulesoftServiceDTO = buildMulesoftServiceDTO(
      offeringId,
      knowledge,
      this.formOverview,
      this.formDetails,
      this.formFunctionality,
      this.formPermissions,
      false,
    );

    this.isDraftSaving.set(true);

    const request$ = offeringUUID
      ? this._mulesoftService.editProfessionalService(mulesoftServiceDTO)
      : this._mulesoftService.createProfessionalService(mulesoftServiceDTO);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        this.isDraftSaving.set(false);
        this.formOverview.markAsPristine();
        this.formDetails.markAsPristine();
        this.formFunctionality.markAsPristine();
        this.formPermissions.markAsPristine();

        this._uiService.showSuccessToast(
          'Your main offering content has been saved',
          'Success',
        );

        this.handleDraftEvent();
        this.markFormAsSavedOrPublished();
        if (offeringUUID) {
          // Do NOT re-read after a write — the read endpoint is cache-lagged
          // and would revert the form to stale data. Overlay the authoritative
          // write response instead; the form already holds the saved values.
          this.syncAfterSave(response);
        } else if (response?.offering?.uuid) {
          this._offeringsService.upsertDraftOptimistic(response.offering);
          this._router.navigate(
            ['/focus/offering/professional-service', response.offering.uuid],
            { replaceUrl: true },
          );
        }
      },
      error: (err) => {
        this.isDraftSaving.set(false);
        if (isDuplicateNameError(err)) {
          this._uiService.showErrorToast(
            POPUPS.DUPLICATE_NAME.DUPLICATE_NAME_TITLE,
            POPUPS.DUPLICATE_NAME.DUPLICATE_OFFERING_DESCRIPTION,
          );
        } else {
          this._uiService.showErrorToast(
            'There was an error saving your offering',
            'Error',
          );
        }
        console.error('MuleSoft draft save error:', err);
        this.handleErrorEvent();
      },
    });
  }

  // Validation

  private validateDraft(): boolean {
    const displayName = this.formOverview.get('displayName');
    const ownerEmail = this.formPermissions.get('ownerEmail');
    const deputyEmail = this.formPermissions.get('deputyEmail');
    const authorEmails = this.formPermissions.get('authorEmails');

    displayName?.markAsTouched();
    ownerEmail?.markAsTouched();
    deputyEmail?.markAsTouched();
    authorEmails?.markAsTouched();

    if (!displayName?.value || displayName.value.trim() === '') {
      return false;
    }
    if (!ownerEmail?.value || ownerEmail.value.trim() === '') {
      return false;
    }
    if (!deputyEmail?.value || deputyEmail.value.trim() === '') {
      return false;
    }

    this.formPermissions.updateValueAndValidity();
    return this.formPermissions.valid;
  }

  private validateAllForms(): boolean {
    // Clear previous errors
    this.validationErrors = [];

    this.markFormGroupTouched(this.formPermissions);
    this.markFormGroupTouched(this.formOverview);
    this.markFormGroupTouched(this.formDetails);
    this.markFormGroupTouched(this.formFunctionality);

    // Helper function to extract and format invalid control names
    const collectInvalidFields = (form: any) => {
      if (form.invalid) {
        Object.keys(form.controls).forEach((key) => {
          if (form.get(key)?.invalid) {
            // Converts key (e.g., 'ownerEmail' -> 'Owner Email')
            const formattedKey = key
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (str) => str.toUpperCase());

            this.validationErrors.push(formattedKey);
          }
        });
      }
    };

    collectInvalidFields(this.formPermissions);
    collectInvalidFields(this.formOverview);
    collectInvalidFields(this.formDetails);
    collectInvalidFields(this.formFunctionality);

    // Table-based validations
    if (this.onboardingSteps().length === 0) {
      this.validationErrors.push('Onboarding Steps');
    }
    if (this.servicesAndRequests().length === 0) {
      this.validationErrors.push('Services And Requests');
    }
    if (this.capabilities().length === 0) {
      this.validationErrors.push('Major Capabilities');
    }

    // Return true ONLY if there are no errors collected
    return this.validationErrors.length === 0;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    for (const key of Object.keys(formGroup.controls)) {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control && typeof control === 'object' && 'controls' in control) {
        this.markFormGroupTouched(control as FormGroup);
      }
    }
  }

  hasError(formGroup: FormGroup, fieldName: string): boolean {
    const control = formGroup.get(fieldName);
    return control ? control.invalid && control.touched : false;
  }

  // Navigation
  @ViewChild('stepper') stepper: any;
  goTo(index: number): void {
    if (this.stepper) {
      this.stepper.selectedIndex = index;
    }
  }

  // Filter function
  filterBy: RdsSingleSelectFilterByFunc<string> = (
    text: string | null,
    item: RdsSingleSelectOptionComponent<string>,
  ): boolean => {
    if (text === null || text === '') {
      return true;
    }
    return item.value.toLowerCase().includes(text.toLowerCase());
  };

  readonly filterByOfferingName: RdsSingleSelectFilterByFunc<string> = (
    text: string | null,
    item: RdsSingleSelectOptionComponent<string>,
  ): boolean => {
    if (text === null || text === '') return true;
    const offering = this.offerings().find((o) => o.uuid === item.value);
    return (
      offering?.displayName.toLowerCase().includes(text.toLowerCase()) ?? false
    );
  };

  // Author management
  addAuthor(event: RdsChipInputEvent<any>): void {
    const value = (event.value ?? '').trim().toLowerCase();
    if (value) {
      this.authors.update((authors) => [...authors, value]);
      this.formPermissions.patchValue({ authorEmails: this.authors() });
    }
    event.chipInput?.clear();
  }

  removeAuthor(author: string): void {
    this.authors.update((authors) => authors.filter((a) => a !== author));
    this.formPermissions.patchValue({ authorEmails: this.authors() });
  }

  onEditAuthor(author: string, event: RdsChipEditedEvent<string>): void {
    const value = event.value.trim().toLowerCase();
    if (!value) {
      this.removeAuthor(author);
      return;
    }

    this.authors.update((authors) => {
      const index = authors.indexOf(author);
      if (index >= 0) {
        const newAuthors = [...authors];
        newAuthors[index] = value;
        return newAuthors;
      }
      return authors;
    });
    this.formPermissions.patchValue({ authorEmails: this.authors() });
  }

  // Slug validation
  private readonly slugValidationEffect = effect(() => {
    const validator = slugValidator(this.offerings(), this.offeringSlug());
    this.formOverview.controls.displayName.setValidators([
      Validators.required,
      validator,
    ]);
    this.formOverview.controls.displayName.updateValueAndValidity({
      emitEvent: false,
    });
  });

  readonly generatedSlug = computed(() => {
    const displayName = this.overviewSig().displayName;
    return displayName ? slugify(displayName) : '';
  });

  getContentStatusLabel(publishedAt?: string | null, updatedAt?: string | null): string {
    // Draft: The offering has never been published
    if (!publishedAt) {
      return 'Draft';
    }

    const pubTime = new Date(publishedAt).getTime();
    const updTime = updatedAt ? new Date(updatedAt).getTime() : 0;

    // Has changes: Published, but updated more recently
    if (updTime > pubTime) {
      return 'Has changes';
    }

    // Published: Published, with no newer updates pending
    return 'Published';
  }
}
